import React, { useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import styles from "./Bulkstatuschangemodal.module.css";
import AxiosInstance from "../../../../services/Api";
import { CustomButton, CustomTextInput } from "../../../../components";

const BulkStatusChangeModal = ({
  show,
  onHide,
  type = "approve",
  selectedRows = [],
  translations: t,
  onSuccess,
  onError,
  fetchAdmissionList,
  admissionType,
}) => {
  const [feedback, setFeedback] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isApprove = type === "approve";

  /* ───── derived labels ───── */
  const modalTitle = isApprove
    ? t?.approveApplication || "Approve Application"
    : t?.rejectStudents || "Reject Students";

  const actionLabel = isApprove
    ? t?.approve || "Approve"
    : t?.reject || "Reject";

  const studentCountText = (
    t?.feedbackRecordedFor ||
    "This feedback will be recorded for {count} student(s)."
  ).replace("{count}", selectedRows.length);

  /* ───── handlers ───── */
  const resetForm = useCallback(() => {
    setFeedback("");
    setFeedbackError("");
    setSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onHide();
  }, [onHide, resetForm]);

  const handleFeedbackChange = useCallback((e) => {
    const { value } = e.target;
    setFeedback(value);
    if (value.trim()) setFeedbackError("");
  }, []);

  const handleSubmit = useCallback(async () => {
    /* ── validate ── */
    if (!feedback.trim()) {
      setFeedbackError(t?.feedbackRequired || "Feedback / Remarks is required");
      return;
    }

    if (!selectedRows.length) {
      onError?.(t?.noStudentsSelected || "No students selected");
      return;
    }

    setSubmitting(true);

    try {
      // Fixed: Approved for approve, Rejected for reject
      const statusCode = isApprove ? "Approved" : "Rejected";

      const payload = {
        ids: selectedRows.map((row) => row.id),
        status: statusCode,
        reason: feedback.trim(),
      };

      const endpoint =
        admissionType === "secondry"
          ? "/admission/secondry/bulk-status-change"
          : "/admission/higherSecondry/bulk-status-change";

      const response = await AxiosInstance.put(endpoint, payload);

      if (response?.data?.status) {
        onSuccess?.();
        handleClose();
        fetchAdmissionList();
      } else {
        const msg =
          response?.data?.message ||
          t?.bulkStatusChangeFailed ||
          "Failed to update status. Please try again.";

        onError?.(msg);
      }
    } catch (error) {
      console.error("Bulk status change error:", error);
      const msg =
        t?.bulkStatusChangeFailed ||
        "Failed to update status. Please try again.";
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  }, [
    feedback,
    selectedRows,
    isApprove,
    t,
    onSuccess,
    onError,
    handleClose,
    fetchAdmissionList,
    admissionType,
  ]);

  /* ───── render ───── */
  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      dialogClassName={styles.modalDialog}
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>{modalTitle}</Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        <div className={styles.fieldWrapper}>
          <CustomTextInput
            label={t?.feedbackRemarks || "Feedback / Remarks"}
            name="feedback"
            value={feedback}
            onChange={handleFeedbackChange}
            placeholder={
              t?.feedbackRemarksPlaceholder || "Enter feedback / remarks"
            }
            error={feedbackError}
            required
            multiline
            rows={4}
          />
        </div>

        <p className={styles.studentCount}>{studentCountText}</p>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <CustomButton
          type="button"
          variant="contained"
          onClick={handleClose}
          fullWidth={false}
          className={styles.cancelButton}
        >
          {t?.cancel || "Cancel"}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          onClick={handleSubmit}
          fullWidth={false}
          loading={submitting}
          className={isApprove ? styles.approveButton : styles.rejectButton}
        >
          {actionLabel}
        </CustomButton>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkStatusChangeModal;
